import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Order, OrderStatus } from '../../types';
import { apiService } from '../../services/api.service';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import { COMMUNITY_TICKET_NOTES, estimateBulkOrderPrice, OrderPricingOption } from './orderPricing';

interface OrderManagementPageProps {
    onSelectOrder: (order: Order) => void;
    onToggleIntegrationPriority: (order: Order) => Promise<void>;
    onCancelOrder: (order: Order) => Promise<void>;
    onUpdatePendingOrder: (order: Order, code: string, totalSteps: number) => Promise<Order>;
    onRequestRedo: (order: Order, reason: string) => Promise<Order>;
    onConfirmOrder: (order: Order) => Promise<Order>;
    onCancelOrderConfirmation: (order: Order) => Promise<Order>;
    onApproveRedo: (order: Order) => Promise<void>;
    onRequestRefund: (order: Order, reason: string) => Promise<Order>;
    onApproveRefund: (order: Order) => Promise<void>;
    canPrioritizeOrders: boolean;
    canCancelOrders: boolean;
    canEditPendingOrders: boolean;
    canRequestRedo: boolean;
    canApproveRedo: boolean;
    canRequestRefund: boolean;
    canApproveRefund: boolean;
    orderCode: string;
    onOrderCodeChange: (value: string) => void;
    orderSteps: number;
    onOrderStepsChange: (steps: number) => void;
    orderPricing: OrderPricingOption[];
    onCreateOrder: () => void;
    isCreating: boolean;
    createError: string;
    createMessage: string;
    canCreateOrder: boolean;
    maintenanceEnabled: boolean | null;
}

interface OrderSummary {
    total: number;
    successfulSteps: number;
    failedSteps: number;
    paidOrders: number;
    unpaidOrders: number;
    unpaidSuccessfulSteps: number;
    paidTotal: number;
    unpaidTotal: number;
}

interface OrderCreatorOption {
    _id: string;
    username: string;
    fullName?: string;
    role: string;
}

const ORDERS_PER_PAGE = 25;
const JUMP_STEPS_PER_RUN = 38;
const PRICING_MENU_MAX_HEIGHT = 240;

const formatAmount = (amount: number) => `${Number(amount || 0).toLocaleString('vi-VN')} đ`;
const getOrderFailureReason = (order: Order) =>
    order.error || order.stepErrors?.[order.stepErrors.length - 1]?.reason;

const statusLabel: Record<string, string> = {
    [OrderStatus.PENDING]: 'Đang chờ',
    [OrderStatus.CODE_RECEIVED]: 'Đã nhận mã',
    [OrderStatus.PROCESSING]: 'Đang xử lý',
    [OrderStatus.COMPLETED]: 'Thành công',
    [OrderStatus.ERROR]: 'Thất bại',
    [OrderStatus.CANCELED]: 'Đã hủy',
};

const statusClass: Record<string, string> = {
    [OrderStatus.PENDING]: 'bg-yellow-100 text-yellow-800',
    [OrderStatus.CODE_RECEIVED]: 'bg-indigo-100 text-indigo-800',
    [OrderStatus.PROCESSING]: 'bg-blue-100 text-blue-800',
    [OrderStatus.COMPLETED]: 'bg-green-100 text-green-800',
    [OrderStatus.ERROR]: 'bg-red-100 text-red-800',
    [OrderStatus.CANCELED]: 'bg-gray-100 text-gray-700',
};

const OrderManagementPage: React.FC<OrderManagementPageProps> = ({
    onSelectOrder,
    onToggleIntegrationPriority,
    onCancelOrder,
    onUpdatePendingOrder,
    onRequestRedo,
    onConfirmOrder,
    onCancelOrderConfirmation,
    onApproveRedo,
    onRequestRefund,
    onApproveRefund,
    canPrioritizeOrders,
    canCancelOrders,
    canEditPendingOrders,
    canRequestRedo,
    canApproveRedo,
    canRequestRefund,
    canApproveRefund,
    orderCode,
    onOrderCodeChange,
    orderSteps,
    onOrderStepsChange,
    orderPricing,
    onCreateOrder,
    isCreating,
    createError,
    createMessage,
    canCreateOrder,
    maintenanceEnabled,
}) => {
    const { user } = useAuth();
    const { socketService } = useSocket();
    const selectedOrderPricing = orderPricing.find((item) => item.totalSteps === orderSteps);
    const shockComboPricing = orderPricing.find((item) => item.totalSteps === 264);
    const orderPriceEstimate = estimateBulkOrderPrice(orderCode, orderSteps, orderPricing);
    const [walletBalance, setWalletBalance] = useState<number | null>(null);
    const [mobileOrderView, setMobileOrderView] = useState<'list' | 'create'>(canCreateOrder ? 'create' : 'list');
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
    const [isOrderStatsVisible, setIsOrderStatsVisible] = useState(false);
    const [searchInput, setSearchInput] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [paymentFilter, setPaymentFilter] = useState('');
    const [creatorRoleFilter, setCreatorRoleFilter] = useState('');
    const [creatorFilter, setCreatorFilter] = useState('');
    const [orderCreators, setOrderCreators] = useState<OrderCreatorOption[]>([]);
    const [isLoadingOrderCreators, setIsLoadingOrderCreators] = useState(false);
    const [orderCreatorsError, setOrderCreatorsError] = useState('');
    const [page, setPage] = useState(1);
    const [visibleOrders, setVisibleOrders] = useState<Order[]>([]);
    const [totalOrders, setTotalOrders] = useState(0);
    const [summary, setSummary] = useState<OrderSummary>({
        total: 0,
        successfulSteps: 0,
        failedSteps: 0,
        paidOrders: 0,
        unpaidOrders: 0,
        unpaidSuccessfulSteps: 0,
        paidTotal: 0,
        unpaidTotal: 0,
    });
    const [isLoadingOrders, setIsLoadingOrders] = useState(true);
    const [ordersError, setOrdersError] = useState('');
    const [refreshVersion, setRefreshVersion] = useState(0);
    const [updatingPriorityId, setUpdatingPriorityId] = useState<string | null>(null);
    const [cancellingOrderId, setCancellingOrderId] = useState<string | null>(null);
    const [editingOrder, setEditingOrder] = useState<Order | null>(null);
    const [editCode, setEditCode] = useState('');
    const [editTotalSteps, setEditTotalSteps] = useState(1);
    const [editError, setEditError] = useState('');
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [showBulkOrderHelp, setShowBulkOrderHelp] = useState(false);
    const [isPricingMenuOpen, setIsPricingMenuOpen] = useState(false);
    const [pricingMenuPosition, setPricingMenuPosition] = useState({
        top: 0,
        left: 0,
        width: 0,
        maxHeight: 0,
        backgroundColor: '#fff',
        color: '#111827',
        borderColor: '#e5e7eb',
        isDarkTheme: false,
    });
    const [requestOrder, setRequestOrder] = useState<Order | null>(null);
    const [requestType, setRequestType] = useState<'redo' | 'refund' | ''>('');
    const [requestCategory, setRequestCategory] = useState<'not_received' | 'insufficient' | 'other' | ''>('');
    const [customRequestReason, setCustomRequestReason] = useState('');
    const [requestError, setRequestError] = useState('');
    const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
    const [approvingRedoId, setApprovingRedoId] = useState<string | null>(null);
    const [approvingRefundId, setApprovingRefundId] = useState<string | null>(null);
    const [confirmingOrderId, setConfirmingOrderId] = useState<string | null>(null);
    const [copiedOrderId, setCopiedOrderId] = useState<string | null>(null);
    const helpButtonRef = useRef<HTMLButtonElement>(null);
    const helpPopupRef = useRef<HTMLDivElement>(null);
    const pricingButtonRef = useRef<HTMLButtonElement>(null);
    const pricingMenuRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (!isPricingMenuOpen) return;

        const revealSpaceBelow = () => {
            const button = pricingButtonRef.current;
            if (!button) return;

            const rect = button.getBoundingClientRect();
            const roomBelow = window.innerHeight - rect.bottom - 8;
            const scrollDistance = PRICING_MENU_MAX_HEIGHT - roomBelow;
            if (scrollDistance > 0) {
                let scrollContainer: HTMLElement | null = button.parentElement;
                while (scrollContainer && scrollContainer !== document.body) {
                    const overflowY = window.getComputedStyle(scrollContainer).overflowY;
                    if (/(auto|scroll)/.test(overflowY) && scrollContainer.scrollHeight > scrollContainer.clientHeight) {
                        scrollContainer.scrollTop += Math.min(
                            scrollDistance,
                            scrollContainer.scrollHeight - scrollContainer.clientHeight - scrollContainer.scrollTop,
                        );
                        break;
                    }
                    scrollContainer = scrollContainer.parentElement;
                }
            }
        };

        const updatePosition = () => {
            const rect = pricingButtonRef.current?.getBoundingClientRect();
            if (!rect) return;
            const isDarkTheme = Boolean(document.querySelector('.chat-theme-dark'));
            setPricingMenuPosition({
                top: rect.bottom + 4,
                left: rect.left,
                width: rect.width,
                maxHeight: Math.max(0, Math.min(PRICING_MENU_MAX_HEIGHT, window.innerHeight - rect.bottom - 8)),
                backgroundColor: isDarkTheme ? '#1e293b' : '#fff',
                color: isDarkTheme ? '#e2e8f0' : '#111827',
                borderColor: isDarkTheme ? '#475569' : '#e5e7eb',
                isDarkTheme,
            });
        };

        revealSpaceBelow();
        updatePosition();
        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            if (pricingButtonRef.current?.contains(target) || pricingMenuRef.current?.contains(target)) return;
            setIsPricingMenuOpen(false);
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setIsPricingMenuOpen(false);
        };
        document.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isPricingMenuOpen]);

    useEffect(() => {
        if (user?.role !== 'admin') return;
        let cancelled = false;
        setIsLoadingOrderCreators(true);
        setOrderCreatorsError('');
        apiService.get<OrderCreatorOption[]>('/users/order-creators')
            .then((creators) => {
                if (!cancelled) setOrderCreators(creators);
            })
            .catch((error: any) => {
                if (!cancelled) {
                    setOrderCreatorsError(error.response?.data?.message || error.message || 'Không tải được danh sách người tạo mã chung sức.');
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoadingOrderCreators(false);
            });
        return () => {
            cancelled = true;
        };
    }, [user?.role]);

    useEffect(() => {
        if (!user?._id || isCreating) return;
        let isCurrent = true;
        apiService.get<{ balance: number }>('/transactions/wallet')
            .then((wallet) => {
                if (isCurrent) setWalletBalance(wallet.balance);
            })
            .catch((error) => {
                console.error('Không tải được số dư ví:', error);
                if (isCurrent) setWalletBalance(null);
            });
        return () => {
            isCurrent = false;
        };
    }, [user?._id, isCreating, createMessage]);

    const copyOrderCode = async (order: Order) => {
        try {
            await navigator.clipboard.writeText(order.code);
            setCopiedOrderId(order._id);
            window.setTimeout(() => {
                setCopiedOrderId((currentId) => currentId === order._id ? null : currentId);
            }, 1500);
        } catch (error) {
            console.error('Không sao chép được mã chung sức:', error);
            window.alert('Không thể sao chép mã chung sức. Vui lòng thử lại.');
        }
    };

    useEffect(() => {
        if (!showBulkOrderHelp) return;

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as Node;
            if (helpButtonRef.current?.contains(target) || helpPopupRef.current?.contains(target)) return;
            setShowBulkOrderHelp(false);
        };

        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [showBulkOrderHelp]);

    useEffect(() => {
        let cancelled = false;
        const params = new URLSearchParams({
            limit: String(ORDERS_PER_PAGE),
            offset: String((page - 1) * ORDERS_PER_PAGE),
        });
        if (user?.role !== 'admin' && user?._id) params.set('userId', user._id);
        const normalizedSearch = searchInput.trim();
        if (normalizedSearch) params.set('search', normalizedSearch);
        if (statusFilter) params.set('status', statusFilter);
        if (paymentFilter) params.set('paymentStatus', paymentFilter);
        if (user?.role === 'admin' && creatorFilter) params.set('createdBy', creatorFilter);
        if (user?.role === 'admin' && creatorRoleFilter) params.set('createdByRole', creatorRoleFilter);

        setIsLoadingOrders(true);
        setOrdersError('');
        const summaryParams = new URLSearchParams();
        if (normalizedSearch) summaryParams.set('search', normalizedSearch);
        if (statusFilter) summaryParams.set('status', statusFilter);
        if (paymentFilter) summaryParams.set('paymentStatus', paymentFilter);
        if (user?.role === 'admin' && creatorFilter) summaryParams.set('createdBy', creatorFilter);
        if (user?.role === 'admin' && creatorRoleFilter) summaryParams.set('createdByRole', creatorRoleFilter);
        Promise.all([
            apiService.get<{ total: number; data: Order[] }>(`/orders?${params.toString()}`),
            apiService.get<OrderSummary>(`/orders/summary${summaryParams.size ? `?${summaryParams.toString()}` : ''}`),
        ]).then(([result, nextSummary]) => {
            if (cancelled) return;
            setVisibleOrders(result.data);
            setTotalOrders(result.total);
            setSummary(nextSummary);
        }).catch((error: any) => {
            if (cancelled) return;
            setOrdersError(error.response?.data?.message || error.message || 'Không tải được danh sách mã chung sức.');
        }).finally(() => {
            if (!cancelled) setIsLoadingOrders(false);
        });

        return () => {
            cancelled = true;
        };
    }, [page, searchInput, statusFilter, paymentFilter, creatorRoleFilter, creatorFilter, refreshVersion, user?._id, user?.role]);

    useEffect(() => {
        const handleOrderProgress = (update: Partial<Order> & { orderId?: string }) => {
            const orderId = update._id || update.orderId;
            if (!orderId) return;
            if (user?.role !== 'admin' && update.userId && String(update.userId) !== String(user?._id)) return;

            const orderPatch: Partial<Order> = {
                ...(update.status !== undefined ? { status: update.status } : {}),
                ...(update.progress !== undefined ? { progress: update.progress } : {}),
                ...(update.currentStep !== undefined ? { currentStep: update.currentStep } : {}),
                ...(update.totalSteps !== undefined ? { totalSteps: update.totalSteps } : {}),
                ...(update.message !== undefined ? { message: update.message } : {}),
                ...(update.error !== undefined ? { error: update.error } : {}),
                ...(update.processedSteps !== undefined ? { processedSteps: update.processedSteps } : {}),
                ...(update.successfulSteps !== undefined ? { successfulSteps: update.successfulSteps } : {}),
                ...(update.failedSteps !== undefined ? { failedSteps: update.failedSteps } : {}),
            };
            setVisibleOrders((currentOrders) => currentOrders.map((order) =>
                String(order._id) === String(orderId) ? { ...order, ...orderPatch } : order,
            ));
        };

        socketService.on('order:updated', handleOrderProgress);
        socketService.on('order:status', handleOrderProgress);
        socketService.on('chat:order:status', handleOrderProgress);

        return () => {
            socketService.off('order:updated', handleOrderProgress);
            socketService.off('order:status', handleOrderProgress);
            socketService.off('chat:order:status', handleOrderProgress);
        };
    }, [socketService, user?._id, user?.role]);

    const totalPages = Math.max(1, Math.ceil(totalOrders / ORDERS_PER_PAGE));

    const refreshOrders = () => setRefreshVersion((version) => version + 1);

    useEffect(() => {
        if (createMessage) setRefreshVersion((version) => version + 1);
    }, [createMessage]);

    const toggleIntegrationPriority = async (order: Order) => {
        setUpdatingPriorityId(order._id);
        try {
            await onToggleIntegrationPriority(order);
            refreshOrders();
        } finally {
            setUpdatingPriorityId(null);
        }
    };

    const cancelOrder = async (order: Order) => {
        setCancellingOrderId(order._id);
        try {
            await onCancelOrder(order);
            refreshOrders();
        } finally {
            setCancellingOrderId(null);
        }
    };

    const beginEditOrder = (order: Order) => {
        setEditingOrder(order);
        setEditCode(order.code);
        setEditTotalSteps(order.totalSteps);
        setEditError('');
    };

    const savePendingOrder = async () => {
        if (!editingOrder) return;
        const normalizedCode = editCode.trim();
        if (normalizedCode.length !== 10) {
            setEditError('Mã chung sức phải có đúng 10 ký tự.');
            return;
        }
        setIsSavingEdit(true);
        setEditError('');
        try {
            await onUpdatePendingOrder(editingOrder, normalizedCode, editTotalSteps);
            setEditingOrder(null);
            refreshOrders();
        } catch (error: any) {
            setEditError(error.response?.data?.message || error.message || 'Không thể cập nhật mã chung sức.');
        } finally {
            setIsSavingEdit(false);
        }
    };

    const submitOrderRequest = async () => {
        if (!requestOrder) return;
        if (!requestType) {
            setRequestError('Vui lòng chọn hoàn tiền hoặc làm lại.');
            return;
        }
        const reason = requestCategory === 'not_received'
            ? 'Không nhận được'
            : requestCategory === 'insufficient'
                ? 'Nhận không đủ'
                : customRequestReason.trim();
        if (!requestCategory) {
            setRequestError('Vui lòng chọn lý do.');
            return;
        }
        if (reason.length < 5 || reason.length > 1000) {
            setRequestError(requestCategory === 'other'
                ? 'Vui lòng nhập lý do từ 5 đến 1000 ký tự.'
                : 'Lý do không hợp lệ.');
            return;
        }

        setIsSubmittingRequest(true);
        setRequestError('');
        try {
            const updatedOrder = requestType === 'redo'
                ? await onRequestRedo(requestOrder, reason)
                : await onRequestRefund(requestOrder, reason);
            setVisibleOrders((currentOrders) => currentOrders.map((order) =>
                order._id === updatedOrder._id ? updatedOrder : order,
            ));
            setRequestOrder(null);
            setRequestCategory('');
            setCustomRequestReason('');
        } catch (error) {
            setRequestError(error instanceof Error ? error.message : 'Không thể gửi yêu cầu.');
        } finally {
            setIsSubmittingRequest(false);
        }
    };

    const confirmOrder = async (order: Order) => {
        setConfirmingOrderId(order._id);
        try {
            const updatedOrder = await onConfirmOrder(order);
            setVisibleOrders((currentOrders) => currentOrders.map((currentOrder) =>
                currentOrder._id === updatedOrder._id ? updatedOrder : currentOrder,
            ));
        } catch (error) {
            window.alert(error instanceof Error ? error.message : 'Không thể xác nhận mã chung sức.');
        } finally {
            setConfirmingOrderId(null);
        }
    };

    const cancelOrderConfirmation = async (order: Order) => {
        setConfirmingOrderId(order._id);
        try {
            const updatedOrder = await onCancelOrderConfirmation(order);
            setVisibleOrders((currentOrders) => currentOrders.map((currentOrder) =>
                currentOrder._id === updatedOrder._id ? updatedOrder : currentOrder,
            ));
        } catch (error) {
            window.alert(error instanceof Error ? error.message : 'Không thể hủy xác nhận mã chung sức.');
        } finally {
            setConfirmingOrderId(null);
        }
    };

    const approveRedoRequest = async (order: Order) => {
        setApprovingRedoId(order._id);
        try {
            await onApproveRedo(order);
            refreshOrders();
        } catch (error) {
            setOrdersError(error instanceof Error ? error.message : 'Không thể duyệt yêu cầu làm lại.');
        } finally {
            setApprovingRedoId(null);
        }
    };

    const approveRefundRequest = async (order: Order) => {
        setApprovingRefundId(order._id);
        try {
            await onApproveRefund(order);
            refreshOrders();
        } catch (error) {
            setOrdersError(error instanceof Error ? error.message : 'Không thể duyệt yêu cầu hoàn tiền.');
        } finally {
            setApprovingRefundId(null);
        }
    };

    return (
        <>
        {maintenanceEnabled !== false && (
            <div role="status" className={`px-4 py-3 text-sm ${maintenanceEnabled ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-700'}`}>
                {maintenanceEnabled
                    ? 'Hệ thống đang bảo trì. Tạm thời không thể tạo mã chung sức; bạn vẫn có thể xem và quản lý các mã đã tạo.'
                    : 'Không tải được trạng thái bảo trì. Tạm thời không thể tạo mã chung sức.'}
            </div>
        )}
        <div className={`grid h-full min-h-0 min-w-0 flex-1 grid-cols-1 overflow-hidden bg-gray-50 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] ${canCreateOrder ? 'grid-rows-[auto_minmax(0,1fr)] md:grid-rows-1' : 'grid-rows-1'}`}>
            {requestOrder && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="presentation">
                    <section
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="order-request-title"
                        className="w-full max-w-lg rounded-xl border border-gray-200 bg-white p-5 shadow-xl"
                    >
                        <h2 id="order-request-title" className="text-lg font-semibold text-gray-900">
                            {requestType === 'redo' ? 'Báo lỗi mã chung sức' : requestType === 'refund' ? 'Yêu cầu hoàn tiền' : 'Báo lỗi mã chung sức'}
                        </h2>
                        <p className="mt-1 text-sm text-gray-500">
                            {requestType === 'redo'
                                ? `Mã chung sức ${requestOrder.code} đã hoàn thành. Admin sẽ xem xét lỗi; nếu được duyệt, mã sẽ được làm lại miễn phí.`
                                : requestType === 'refund'
                                    ? `Mã chung sức ${requestOrder.code} đã hoàn thành. Admin cần duyệt trước khi ${formatAmount(requestOrder.paidAmount || 0)} được hoàn vào ví.`
                                    : `Chọn cách xử lý và lý do cho mã chung sức ${requestOrder.code}. Yêu cầu sẽ được gửi admin duyệt.`}
                        </p>
                        <label htmlFor="order-request-type" className="mt-4 block text-sm font-medium text-gray-700">Mục yêu cầu</label>
                        <select
                            id="order-request-type"
                            value={requestType}
                            onChange={(event) => {
                                setRequestType(event.target.value as typeof requestType);
                                setRequestError('');
                            }}
                            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        >
                            <option value="">Chọn mục</option>
                            <option value="refund">Hoàn tiền</option>
                            <option value="redo">Làm lại</option>
                        </select>
                        <label htmlFor="order-request-category" className="mt-4 block text-sm font-medium text-gray-700">Lý do</label>
                        <select
                            id="order-request-category"
                            value={requestCategory}
                            onChange={(event) => {
                                setRequestCategory(event.target.value as typeof requestCategory);
                                setRequestError('');
                            }}
                            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        >
                            <option value="">Chọn lý do</option>
                            <option value="not_received">Không nhận được</option>
                            <option value="insufficient">Nhận không đủ</option>
                            <option value="other">Khác</option>
                        </select>
                        {requestCategory === 'other' && (
                            <textarea
                                id="order-request-custom-reason"
                                aria-label="Nhập lý do khác"
                                value={customRequestReason}
                                onChange={(event) => setCustomRequestReason(event.target.value)}
                                maxLength={1000}
                                rows={3}
                                placeholder="Nhập lý do cụ thể (5-1000 ký tự)..."
                                className="mt-3 w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                            />
                        )}
                        {requestError && <p role="alert" className="mt-2 text-sm text-red-600">{requestError}</p>}
                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setRequestOrder(null)}
                                disabled={isSubmittingRequest}
                                className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                                Hủy
                            </button>
                            <button
                                type="button"
                                onClick={() => void submitOrderRequest()}
                                disabled={isSubmittingRequest}
                                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${requestType === 'refund' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                            >
                                {isSubmittingRequest ? 'Đang gửi...' : 'Gửi yêu cầu'}
                            </button>
                        </div>
                    </section>
                </div>
            )}
            {editingOrder && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="presentation">
                    <section
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="edit-pending-order-title"
                        className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-5 shadow-xl"
                    >
                        <h2 id="edit-pending-order-title" className="text-lg font-semibold text-gray-900">Sửa mã chung sức đang chờ</h2>
                        <label className="mt-4 block text-sm font-medium text-gray-700" htmlFor="pending-order-code">Mã chung sức</label>
                        <input
                            id="pending-order-code"
                            value={editCode}
                            onChange={(event) => setEditCode(event.target.value)}
                            maxLength={10}
                            autoComplete="off"
                            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        />
                        <label className="mt-4 block text-sm font-medium text-gray-700" htmlFor="pending-order-steps">Số lượt chạy</label>
                        <select
                            id="pending-order-steps"
                            value={editTotalSteps}
                            onChange={(event) => setEditTotalSteps(Number(event.target.value))}
                            className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                        >
                            {!orderPricing.some((item) => item.totalSteps === editingOrder.totalSteps) && (
                                <option value={editingOrder.totalSteps}>{editingOrder.totalSteps} lượt (hiện tại)</option>
                            )}
                            {orderPricing.map((item) => (
                                <option key={item.totalSteps} value={item.totalSteps}>
                                    {item.title ? `${item.title} · ` : ''}{item.totalSteps} lượt · {item.discountPercent ? `${(item.originalPrice ?? item.price).toLocaleString('vi-VN')} → ${(item.discountedPrice ?? item.price).toLocaleString('vi-VN')}` : item.price.toLocaleString('vi-VN')} đ
                                </option>
                            ))}
                        </select>
                        <p className="mt-2 text-xs text-gray-500">Nếu số lượt thay đổi, hệ thống sẽ điều chỉnh tiền theo giá mới.</p>
                        {editError && <p role="alert" className="mt-3 text-sm text-red-600">{editError}</p>}
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setEditingOrder(null)}
                                disabled={isSavingEdit}
                                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                                Đóng
                            </button>
                            <button
                                type="button"
                                onClick={() => void savePendingOrder()}
                                disabled={isSavingEdit}
                                className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-wait disabled:opacity-50"
                            >
                                {isSavingEdit ? 'Đang lưu...' : 'Lưu thay đổi'}
                            </button>
                        </div>
                    </section>
                </div>
            )}
            {canCreateOrder && (
                <div className="col-start-1 row-start-1 flex gap-2 border-b border-gray-200 bg-white p-2 md:hidden">
                    <button
                        type="button"
                        onClick={() => setMobileOrderView('list')}
                        aria-pressed={mobileOrderView === 'list'}
                        className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold ${mobileOrderView === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                    >
                        Kiểm tra vé vào acc
                    </button>
                    <button
                        type="button"
                        onClick={() => setMobileOrderView('create')}
                        aria-pressed={mobileOrderView === 'create'}
                        className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold ${mobileOrderView === 'create' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                    >
                        Bắt đầu
                    </button>
                </div>
            )}
            {canCreateOrder && (
                <div className={`col-start-1 row-start-2 min-h-0 overflow-auto p-2 md:col-start-1 md:row-start-1 md:block md:p-6 ${mobileOrderView === 'create' ? 'block' : 'hidden'}`}>
                    <section className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm md:p-5">
                        {shockComboPricing && (
                            <button
                                type="button"
                                onClick={() => onOrderStepsChange(264)}
                                aria-pressed={orderSteps === 264}
                                className={`mb-4 flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-3 text-left transition md:px-4 ${
                                    orderSteps === 264
                                        ? 'border-orange-400 bg-orange-50 ring-2 ring-orange-100'
                                        : 'border-orange-200 bg-orange-50/60 hover:border-orange-400 hover:bg-orange-50'
                                }`}
                            >
                                <span>
                                    <span className="block text-sm font-semibold text-gray-900">Combo sốc</span>
                                </span>
                                <span className="shrink-0 text-right">
                                    <span className="block text-xs text-gray-600">
                                        {`${shockComboPricing.totalSteps * 38} vé`}
                                    </span>
                                    {shockComboPricing.discountPercent ? (
                                        <>
                                            <del className="block text-xs text-gray-500">
                                                {formatAmount(shockComboPricing.originalPrice ?? shockComboPricing.price)}
                                            </del>
                                            <strong className="text-sm font-semibold text-orange-700">
                                                {formatAmount(shockComboPricing.discountedPrice ?? shockComboPricing.price)}
                                            </strong>
                                        </>
                                    ) : (
                                        <strong className="text-sm font-semibold text-orange-700">
                                            {formatAmount(shockComboPricing.price)}
                                        </strong>
                                    )}
                                </span>
                            </button>
                        )}
                        <div>
                            <h2 className="text-base font-semibold text-gray-900 md:text-lg">Nhập mã chung sức</h2>
                            <p className="mt-1 text-xs text-gray-500 md:text-sm">Khách vào chung sức gần chổ nhảy - bấm vào chữ nhận - sao chép mã dán vào đây</p>
                        </div>
                        <div className="mt-4 space-y-3">
                            <div className="relative">
                                <textarea
                                    value={orderCode}
                                    onChange={(event) => onOrderCodeChange(event.target.value)}
                                    placeholder={'Nhập mã chung sức (1 mã có thể nhận không giới hạn số vé)\nVí dụ: bYG3qj5LuZ\nví dụ: Chia sẻ mã mời --4UV8nH6MG9--, [[llyqQjpxfH724s7HMEtSLZFr3ERjDPP ]]'}
                                    rows={4}
                                    aria-label="Mã chung sức và số lần"
                                    className="block w-full resize-none rounded-lg border border-gray-200 px-3 py-2 pr-12 font-mono text-xs outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 md:text-sm"
                                />
                                <button
                                    type="button"
                                    ref={helpButtonRef}
                                    onClick={() => setShowBulkOrderHelp((visible) => !visible)}
                                    className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 text-xs font-bold text-gray-500 hover:border-blue-500 hover:text-blue-600"
                                    aria-label="Cách nhập nhiều mã chung sức"
                                    title="Cách nhập nhiều mã chung sức"
                                >
                                    ?
                                </button>
                                {showBulkOrderHelp && (
                                    <div ref={helpPopupRef} className="absolute left-0 right-0 top-full z-20 mt-1 rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs text-blue-800 shadow-lg">
                                        <p className="font-semibold">Cách nhập nhiều mã chung sức</p>
                                        <p className="mt-1">Mỗi dòng nhập một mã dài đúng 10 ký tự. Có thể dán nguyên văn mã mời của tôi, mã sẽ được lấy từ phần nằm giữa hai dấu --.</p>
                                        <p className="mt-1">Một mã riêng lẻ dùng số lần đã chọn bên dưới. Khi nhập nhiều dòng, số lượng sau | là tùy chọn và mặc định là 1.</p>
                                        <p className="mt-1 font-mono">Mã mời của tôi là --bYG3qj5LuZ--, [[llE5k3JYHDX2c1LepQQtfjv0KT5ghPP ]]</p>
                                        <p className="font-mono">bYG3qj5LuZ</p>
                                        <p className="font-mono">bYG3qj5LuZ|2</p>
                                    </div>
                                )}
                            </div>
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                                <div className="relative min-w-0 sm:w-72">
                                    <button
                                        type="button"
                                        ref={pricingButtonRef}
                                        onClick={() => setIsPricingMenuOpen((open) => !open)}
                                        aria-label="Số lần xử lý"
                                        aria-haspopup="listbox"
                                        aria-expanded={isPricingMenuOpen}
                                        className="flex w-full min-w-0 items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-xs outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 md:text-sm"
                                    >
                                        <span className="truncate">
                                            {selectedOrderPricing
                                                ? `${selectedOrderPricing.title ? `${selectedOrderPricing.title} · ` : ''}${selectedOrderPricing.totalSteps} lần · ${selectedOrderPricing.discountPercent ? `${(selectedOrderPricing.originalPrice ?? selectedOrderPricing.price).toLocaleString('vi-VN')} → ${(selectedOrderPricing.discountedPrice ?? selectedOrderPricing.price).toLocaleString('vi-VN')}` : selectedOrderPricing.price.toLocaleString('vi-VN')} đ`
                                                : orderPricing.length > 0 ? 'Chọn số lần xử lý' : 'Chưa có bảng giá'}
                                        </span>
                                        <span aria-hidden="true" className="shrink-0">Chọn vé</span>
                                    </button>
                                    {isPricingMenuOpen && createPortal(
                                        <div
                                            ref={pricingMenuRef}
                                            role="listbox"
                                            aria-label="Số lần xử lý"
                                            className={`overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-xl ${pricingMenuPosition.isDarkTheme ? 'pricing-menu-dark' : ''}`}
                                            style={{
                                                position: 'fixed',
                                                top: pricingMenuPosition.top,
                                                left: pricingMenuPosition.left,
                                                width: pricingMenuPosition.width,
                                                maxHeight: pricingMenuPosition.maxHeight,
                                                zIndex: 1000,
                                                backgroundColor: pricingMenuPosition.backgroundColor,
                                                color: pricingMenuPosition.color,
                                                borderColor: pricingMenuPosition.borderColor,
                                            }}
                                        >
                                            {orderPricing.length > 0 ? orderPricing.map((item) => (
                                                <button
                                                    key={item.totalSteps}
                                                    type="button"
                                                    role="option"
                                                    aria-selected={orderSteps === item.totalSteps}
                                                    onClick={() => {
                                                        onOrderStepsChange(item.totalSteps);
                                                        setIsPricingMenuOpen(false);
                                                    }}
                                                    className={`pricing-menu-option block w-full px-3 py-2 text-left text-xs md:text-sm ${pricingMenuPosition.isDarkTheme ? '' : 'hover:bg-blue-50'} ${orderSteps === item.totalSteps ? (pricingMenuPosition.isDarkTheme ? 'font-semibold' : 'bg-blue-50 font-semibold') : ''}`}
                                                >
                                                    {item.title ? `${item.title} · ` : ''}{item.totalSteps} lần · {item.discountPercent ? `${(item.originalPrice ?? item.price).toLocaleString('vi-VN')} → ${(item.discountedPrice ?? item.price).toLocaleString('vi-VN')}` : item.price.toLocaleString('vi-VN')} đ
                                                </button>
                                            )) : (
                                                <div className="px-3 py-2 text-xs text-gray-500 md:text-sm">Chưa có bảng giá</div>
                                            )}
                                        </div>,
                                        document.body,
                                    )}
                                </div>
                                {maintenanceEnabled === false && (
                                    <button
                                        type="button"
                                        onClick={onCreateOrder}
                                        disabled={isCreating}
                                        className="min-h-10 rounded-lg bg-blue-600 px-5 py-2 text-xs font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                                    >
                                        {isCreating ? 'Đang tạo...' : 'Băt đầu'}
                                    </button>
                                )}
                            </div>
                            {selectedOrderPricing && (
                                <p className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-gray-600">
                                    <span>
                                        Giá mã chung sức:{' '}
                                        {selectedOrderPricing.discountPercent ? (
                                            <>
                                                <del className="mr-1 text-gray-400">{formatAmount(selectedOrderPricing.originalPrice ?? selectedOrderPricing.price)}</del>
                                                <strong className="text-green-700">{formatAmount(selectedOrderPricing.discountedPrice ?? selectedOrderPricing.price)}</strong>
                                                <span className="ml-1 text-green-700">(VIP -{selectedOrderPricing.discountPercent}%)</span>
                                            </>
                                        ) : <strong>{formatAmount(selectedOrderPricing.price)}</strong>}
                                    </span>
                                    {user?.role !== 'admin' && (
                                        <span className="font-medium text-gray-700">
                                            Số tiền hiện tại: <strong className="text-blue-700">{walletBalance === null ? 'Đang tải...' : formatAmount(walletBalance)}</strong>
                                        </span>
                                    )}
                                </p>
                            )}
                            {orderPriceEstimate && (
                                <p className="text-xs text-gray-600">
                                    Tổng tiền ước tính ({orderPriceEstimate.orderCount} mã chung sức):{' '}
                                    {orderPriceEstimate.originalTotal !== orderPriceEstimate.payableTotal && (
                                        <del className="mr-1 text-gray-400">{formatAmount(orderPriceEstimate.originalTotal)}</del>
                                    )}
                                    <strong className={orderPriceEstimate.originalTotal !== orderPriceEstimate.payableTotal ? 'text-green-700' : ''}>
                                        {formatAmount(orderPriceEstimate.payableTotal)}
                                    </strong>
                                </p>
                            )}
                        </div>
                        {createError && <p role="alert" className="mt-3 text-sm text-red-600">{createError}</p>}
                        {createMessage && <p role="status" className="mt-3 text-sm text-green-700">{createMessage}</p>}
                        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700 md:text-sm">
                            <p className="mt-2 font-semibold text-gray-900">Bảng giá chung sức bên Nhỏ:</p>
                            <p className="font-semibold text-gray-900">Mức tiền nạp càng lớn càng được + thêm nhiều vé nhé ạ.</p>
                            <ul className="mt-1 grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2">
                                {orderPricing.length > 0 ? orderPricing.map((pricing) => (
                                    <li key={pricing.totalSteps}>
                                        {formatAmount(pricing.discountedPrice ?? pricing.price)} = {pricing.totalSteps * 38} vé
                                        {COMMUNITY_TICKET_NOTES[pricing.totalSteps]
                                            ? ` (${COMMUNITY_TICKET_NOTES[pricing.totalSteps]})`
                                            : ''}
                                    </li>
                                )) : <li>Đang tải bảng giá...</li>}
                            </ul>
                            <p className="mt-2">Tỉ lệ nhận được skin SSS rất thấp nhé mọi người.</p>
                            <p className="mt-1">Khách quay nhận được skin SSS thì quay video lại để chứng minh; bên Nhỏ sẽ tặng 1000 vé quay nhé.</p>
                        </div>
                    </section>
                </div>
            )}

            <div className={`col-start-1 ${canCreateOrder ? 'row-start-2' : 'row-start-1'} min-h-0 min-w-0 flex-col gap-2 overflow-hidden p-2 md:col-start-2 md:row-start-1 md:flex md:gap-5 md:p-6 ${mobileOrderView === 'list' ? 'flex' : 'hidden'}`}>
                <div className="flex shrink-0 items-start justify-between gap-2">
                    <div>
                        <h2 className="text-base font-semibold text-gray-900 md:text-xl">Tổng quan mã chung sức</h2>
                        <p className="mt-1 text-[11px] text-gray-500 md:text-sm">Theo dõi trạng thái và tình hình thanh toán của các mã chung sức.</p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                        <button
                            type="button"
                            onClick={() => setIsOrderStatsVisible((visible) => !visible)}
                            aria-label={isOrderStatsVisible ? 'Ẩn thống kê mã chung sức' : 'Hiện thống kê mã chung sức'}
                            aria-expanded={isOrderStatsVisible}
                            title={isOrderStatsVisible ? 'Ẩn thống kê mã chung sức' : 'Hiện thống kê mã chung sức'}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                        >
                            {isOrderStatsVisible ? (
                                <span aria-hidden="true" className="text-lg leading-none">×</span>
                            ) : (
                                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
                                    <path d="M4 19V5" />
                                    <path d="M4 19h17" />
                                    <rect x="7" y="11" width="3" height="5" rx="0.5" />
                                    <rect x="12" y="8" width="3" height="8" rx="0.5" />
                                    <rect x="17" y="5" width="3" height="11" rx="0.5" />
                                </svg>
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsMobileSearchOpen((open) => !open)}
                            aria-label={isMobileSearchOpen ? 'Ẩn bộ lọc mã chung sức' : 'Hiện bộ lọc mã chung sức'}
                            aria-expanded={isMobileSearchOpen}
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 md:hidden"
                        >
                            {isMobileSearchOpen ? (
                                <span aria-hidden="true" className="text-lg leading-none">×</span>
                            ) : (
                                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
                                    <circle cx="11" cy="11" r="7" />
                                    <path d="m20 20-4-4" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>

                <div className="shrink-0">
                    <div className={`${isMobileSearchOpen ? 'flex' : 'hidden'} flex-col gap-2 md:flex lg:flex-row`}>
                        <input
                            value={searchInput}
                            onChange={(event) => {
                                setSearchInput(event.target.value);
                                setPage(1);
                            }}
                            placeholder="Tìm theo mã chung sức..."
                            className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 md:px-4 md:py-3 md:text-sm"
                        />
                        <select
                            value={statusFilter}
                            onChange={(event) => {
                                setStatusFilter(event.target.value);
                                setPage(1);
                            }}
                            aria-label="Lọc theo trạng thái mã chung sức"
                            className="min-w-0 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 md:py-3 md:text-sm lg:w-48"
                        >
                            <option value="">Tất cả trạng thái</option>
                            {Object.entries(statusLabel).map(([status, label]) => (
                                <option key={status} value={status}>{label}</option>
                            ))}
                        </select>
                        <select
                            value={paymentFilter}
                            onChange={(event) => {
                                setPaymentFilter(event.target.value);
                                setPage(1);
                            }}
                            aria-label="Lọc theo thanh toán"
                            className="min-w-0 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 md:py-3 md:text-sm lg:w-48"
                        >
                            <option value="">Tất cả thanh toán</option>
                            <option value="paid">Đã thanh toán</option>
                            <option value="unpaid">Chưa thanh toán</option>
                        </select>
                        {user?.role === 'admin' && (
                            <>
                                <select
                                    value={creatorRoleFilter}
                                    onChange={(event) => {
                                        setCreatorRoleFilter(event.target.value);
                                        setCreatorFilter('');
                                        setPage(1);
                                    }}
                                    aria-label="Lọc theo vai trò người tạo mã"
                                    className="min-w-0 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 md:py-3 md:text-sm lg:w-48"
                                >
                                    <option value="">Tất cả role</option>
                                    {Array.from(new Set(orderCreators.map((creator) => creator.role))).sort().map((role) => (
                                        <option key={role} value={role}>
                                            {role === 'admin' ? 'Quản trị viên' : role === 'partner' ? 'Đối tác' : role === 'user' ? 'Người dùng' : role === 'staff' ? 'Nhân viên' : role}
                                        </option>
                                    ))}
                                </select>
                                <select
                                    value={creatorFilter}
                                    onChange={(event) => {
                                        setCreatorFilter(event.target.value);
                                        setPage(1);
                                    }}
                                    aria-label="Lọc theo người tạo mã"
                                    className="min-w-0 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 md:py-3 md:text-sm lg:w-48"
                                >
                                    <option value="">{isLoadingOrderCreators ? 'Đang tải người tạo...' : 'Tất cả người tạo'}</option>
                                    {orderCreators
                                        .filter((creator) => !creatorRoleFilter || creator.role === creatorRoleFilter)
                                        .map((creator) => (
                                            <option key={creator._id} value={creator.username}>
                                                {creator.fullName ? `${creator.fullName} (@${creator.username})` : creator.username}
                                            </option>
                                        ))}
                                </select>
                            </>
                        )}
                        {searchInput && (
                            <button
                                type="button"
                                onClick={() => {
                                    setSearchInput('');
                                    setPage(1);
                                }}
                                className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 md:px-4 md:py-3 md:text-sm"
                            >
                                Xóa tìm kiếm
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={refreshOrders}
                            disabled={isLoadingOrders}
                            className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 md:px-4 md:py-3 md:text-sm"
                        >
                            Làm mới
                        </button>
                    </div>
                    {orderCreatorsError && user?.role === 'admin' && (
                        <p role="alert" className="mt-2 text-xs text-red-600">{orderCreatorsError}</p>
                    )}
                </div>

                {isOrderStatsVisible && <div className="flex shrink-0 gap-2 overflow-x-auto pb-1 md:grid md:grid-cols-2 md:gap-3 md:overflow-visible md:pb-0 xl:grid-cols-4">
                    <div className="min-w-[120px] rounded-xl border border-gray-200 bg-white p-2 shadow-sm md:min-w-0 md:p-4">
                        <p className="text-xs text-gray-500">Tổng mã chung sức</p>
                        <p className="mt-1 text-xl font-semibold text-gray-900 md:mt-2 md:text-2xl">{summary.total}</p>
                    </div>
                    <div className="min-w-[120px] rounded-xl border border-green-100 bg-green-50 p-2 shadow-sm md:min-w-0 md:p-4">
                        <p className="text-xs text-green-700">Số lượt thành công</p>
                        <p className="mt-1 text-xl font-semibold text-green-700 md:mt-2 md:text-2xl">{summary.successfulSteps}</p>
                    </div>
                    <div className="min-w-[120px] rounded-xl border border-red-100 bg-red-50 p-2 shadow-sm md:min-w-0 md:p-4">
                        <p className="text-xs text-red-700">Số lượt thất bại</p>
                        <p className="mt-1 text-xl font-semibold text-red-700 md:mt-2 md:text-2xl">{summary.failedSteps}</p>
                    </div>
                    <div className="min-w-[120px] rounded-xl border border-green-100 bg-green-50 p-2 shadow-sm md:min-w-0 md:p-4">
                        <p className="text-xs text-green-700">Mã đã thanh toán</p>
                        <p className="mt-1 text-xl font-semibold text-green-700 md:mt-2 md:text-2xl">{summary.paidOrders}</p>
                    </div>
                    <div className="min-w-[120px] rounded-xl border border-orange-100 bg-orange-50 p-2 shadow-sm md:min-w-0 md:p-4">
                        <p className="text-xs text-orange-700">Mã chưa thanh toán</p>
                        <p className="mt-1 text-xl font-semibold text-orange-700 md:mt-2 md:text-2xl">{summary.unpaidOrders}</p>
                    </div>
                    <div className="min-w-[120px] rounded-xl border border-blue-100 bg-blue-50 p-2 shadow-sm md:min-w-0 md:p-4">
                        <p className="text-xs text-blue-700">Đã thanh toán</p>
                        <p className="mt-1 text-sm font-semibold text-blue-700 md:mt-2 md:text-lg">{formatAmount(summary.paidTotal)}</p>
                    </div>
                    <div className="min-w-[120px] rounded-xl border border-orange-100 bg-orange-50 p-2 shadow-sm md:col-span-2 md:min-w-0 md:p-4">
                        <p className="text-xs text-orange-700">Chưa thanh toán</p>
                        <div className="mt-2 grid grid-cols-[1.35fr_0.65fr_1fr] divide-x divide-orange-200">
                            <div className="min-w-0 pr-2">
                                <p className="text-[9px] text-orange-700 md:text-xs">Số tiền</p>
                                <p className="mt-1 whitespace-nowrap text-xs font-semibold text-orange-700 md:text-sm">{formatAmount(summary.unpaidTotal)}</p>
                            </div>
                            <div className="min-w-0 px-2">
                                <p className="text-[9px] text-orange-700 md:text-xs">Số lượt</p>
                                <p className="mt-1 text-xs font-semibold text-orange-700 md:text-sm">{summary.unpaidSuccessfulSteps || 0}</p>
                            </div>
                            <div className="min-w-0 pl-2">
                                <p className="text-[9px] text-orange-700 md:text-xs">vé nhảy</p>
                                <p className="mt-1 text-xs font-semibold text-orange-700 md:text-sm">
                                    {(summary.unpaidSuccessfulSteps || 0) * JUMP_STEPS_PER_RUN}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>}

                <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                    <div className="border-b border-gray-100 px-3 py-3 md:px-4 md:py-4">
                        <h3 className="text-sm font-semibold text-gray-900 md:text-base">Danh sách mã chung sức ({totalOrders})</h3>
                    </div>
                    {ordersError ? (
                        <p role="alert" className="p-8 text-center text-sm text-red-600">{ordersError}</p>
                    ) : (
                        <div className="min-h-0 min-w-0 flex-1 overflow-x-auto overflow-y-auto overscroll-contain">
                            {visibleOrders.length === 0 ? (
                                <p className="p-8 text-center text-sm text-gray-500">
                                    {isLoadingOrders
                                        ? 'Đang tải mã chung sức...'
                                        : searchInput || statusFilter || paymentFilter || creatorRoleFilter || creatorFilter
                                            ? 'Không tìm thấy mã chung sức phù hợp với bộ lọc.'
                                            : 'Chưa có mã chung sức.'}
                                </p>
                            ) : (
                                <table className={`w-full table-fixed text-left text-[10px] md:text-sm ${user?.role === 'admin' ? 'min-w-[1200px]' : 'min-w-[1060px]'}`}>
                                    <thead className="sticky top-0 bg-gray-50 text-[10px] uppercase text-gray-500 md:text-xs">
                                        <tr>
                                            {user?.role === 'admin' && <th className="w-36 px-2 py-2 md:px-4 md:py-3">Người tạo</th>}
                                            <th className="w-36 px-2 py-2 md:px-4 md:py-3">Mã chung sức</th>
                                            <th className="w-32 px-2 py-2 md:px-4 md:py-3">Trạng thái</th>
                                            <th className="w-36 px-2 py-2 md:px-4 md:py-3">Tiến độ</th>
                                            <th className="w-28 px-2 py-2 text-right md:px-4 md:py-3">Giá trị</th>
                                            <th className="w-32 px-2 py-2 text-right md:px-4 md:py-3">Đã thanh toán</th>
                                            <th className="w-28 px-2 py-2 text-right md:px-4 md:py-3">Còn thiếu</th>
                                            <th className="w-32 px-2 py-2 md:px-4 md:py-3">Ngày tạo</th>
                                            {(canPrioritizeOrders || canCancelOrders || canEditPendingOrders || canRequestRedo || canApproveRedo || canRequestRefund || canApproveRefund || visibleOrders.some((order) => Boolean(order.note))) && <th className="w-72 px-2 py-2 text-right md:px-4 md:py-3">Thao tác</th>}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {visibleOrders.map((order) => (
                                            <tr
                                                key={order._id}
                                                onClick={() => onSelectOrder(order)}
                                                className="cursor-pointer hover:bg-blue-50"
                                                title="Xem chi tiết mã chung sức"
                                            >
                                                {user?.role === 'admin' && (
                                                    <td className="w-36 truncate px-2 py-3 font-medium text-gray-700 md:px-4 md:py-4">
                                                        {order.createdBy || order.username || order.userId}
                                                    </td>
                                                )}
                                                <td className="w-36 whitespace-nowrap px-2 py-3 text-[10px] font-medium text-gray-900 md:px-4 md:py-4 md:text-sm">
                                                    <span className="inline-flex max-w-full items-center gap-1">
                                                        {order.code}
                                                        <button
                                                            type="button"
                                                            onClick={(event) => {
                                                                event.stopPropagation();
                                                                void copyOrderCode(order);
                                                            }}
                                                            aria-label={`Sao chép mã chung sức ${order.code}`}
                                                            title={copiedOrderId === order._id ? 'Đã sao chép' : 'Sao chép mã chung sức'}
                                                            className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-blue-600"
                                                        >
                                                            {copiedOrderId === order._id ? (
                                                                <span aria-hidden="true" className="text-xs text-green-600">✓</span>
                                                            ) : (
                                                                <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-3.5 w-3.5">
                                                                    <rect x="8" y="8" width="12" height="12" rx="2" />
                                                                    <path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" />
                                                                </svg>
                                                            )}
                                                        </button>
                                                    </span>
                                                    {order.redoRequestReason && (
                                                        <p className="mt-1 max-w-xs truncate text-xs font-normal text-orange-700" title={order.redoRequestReason}>
                                                            Báo lỗi: {order.redoRequestReason}
                                                        </p>
                                                    )}
                                                    {order.refundRequestReason && (
                                                        <p className="mt-1 max-w-xs truncate text-xs font-normal text-red-700" title={order.refundRequestReason}>
                                                            Lý do hoàn tiền: {order.refundRequestReason}
                                                        </p>
                                                    )}
                                                    {order.status === OrderStatus.ERROR && getOrderFailureReason(order) && (
                                                        <p className="mt-1 max-w-xs truncate text-xs font-normal text-red-700" title={getOrderFailureReason(order)}>
                                                            Lý do: {getOrderFailureReason(order)}
                                                        </p>
                                                    )}
                                                </td>
                                                <td className="w-32 px-2 py-3 md:px-4 md:py-4">
                                                    <span className={`inline-block whitespace-nowrap rounded-full px-2 py-1 text-[10px] font-medium md:px-2.5 md:text-xs ${statusClass[order.status] || 'bg-gray-100 text-gray-700'}`}>
                                                        {order.error === 'Cancelled' ? 'Đã hủy' : statusLabel[order.status] || order.status}
                                                    </span>
                                                </td>
                                                <td className="px-2 py-3 text-gray-700 md:px-4 md:py-4">
                                                    <div className="min-w-28">
                                                        <div className="flex justify-between text-[9px] md:text-xs">
                                                            <span>{order.progress}%</span>
                                                            <span>Lần {order.currentStep}/{order.totalSteps}</span>
                                                        </div>
                                                        <div className="mt-1 h-1.5 rounded-full bg-gray-200">
                                                            <div className="h-1.5 rounded-full bg-blue-500" style={{ width: `${order.progress}%` }} />
                                                        </div>
                                                        <div className="mt-1 text-[9px] text-gray-500 md:text-xs">
                                                            vé nhảy: {Math.max(order.processedSteps || 0, order.currentStep || 0) * JUMP_STEPS_PER_RUN}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-2 py-3 text-right text-[10px] text-gray-700 md:px-4 md:py-4 md:text-sm">{formatAmount(order.price || 0)}</td>
                                                <td className="px-2 py-3 text-right text-[10px] font-medium text-green-600 md:px-4 md:py-4 md:text-sm">{formatAmount(order.paidAmount || 0)}</td>
                                                <td className="px-2 py-3 text-right text-[10px] font-medium text-orange-600 md:px-4 md:py-4 md:text-sm">{formatAmount(order.unpaidAmount || 0)}</td>
                                                <td className="px-2 py-3 text-[10px] text-gray-500 md:px-4 md:py-4 md:text-sm">
                                                    {new Intl.DateTimeFormat('vi-VN', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(order.createdAt))}
                                                </td>
                                                {(canPrioritizeOrders || canCancelOrders || canEditPendingOrders || canRequestRedo || canApproveRedo || canRequestRefund || canApproveRefund || order.note) && (
                                                    <td className="px-2 py-3 text-right md:px-4 md:py-4" onClick={(event) => event.stopPropagation()}>
                                                        <div className="flex flex-wrap justify-end gap-2">
                                                            {canRequestRedo && order.status === OrderStatus.COMPLETED && !order.redoRequestStatus && !order.refundRequestStatus && !order.redoOrderId && (
                                                                <>
                                                                    {!order.customerConfirmed && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                setRequestOrder(order);
                                                                                setRequestType('');
                                                                                setRequestCategory('');
                                                                                setCustomRequestReason('');
                                                                                setRequestError('');
                                                                            }}
                                                                            className="rounded-md border border-orange-200 bg-white px-3 py-1.5 text-xs font-semibold text-orange-700 transition hover:bg-orange-50"
                                                                        >
                                                                            Báo lỗi
                                                                        </button>
                                                                    )}
                                                                    {order.customerConfirmed ? (
                                                                        <>
                                                                            <span className="rounded-md bg-green-100 px-3 py-1.5 text-xs font-semibold text-green-700">
                                                                                Đã OK
                                                                            </span>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => void cancelOrderConfirmation(order)}
                                                                                disabled={confirmingOrderId === order._id}
                                                                                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-red-700 disabled:opacity-50"
                                                                                title="Hủy xác nhận nếu bạn bấm nhầm"
                                                                            >
                                                                                {confirmingOrderId === order._id ? 'Đang hủy...' : 'Hủy OK'}
                                                                            </button>
                                                                        </>
                                                                    ) : (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => void confirmOrder(order)}
                                                                            disabled={confirmingOrderId === order._id}
                                                                            className="rounded-md bg-green-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-green-700 disabled:opacity-50"
                                                                            title="Xác nhận đã nhận mã chung sức"
                                                                        >
                                                                            {confirmingOrderId === order._id ? 'Đang xác nhận...' : 'OK'}
                                                                        </button>
                                                                    )}
                                                                </>
                                                            )}
                                                            {order.redoRequestStatus === 'pending' && canApproveRedo && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => void approveRedoRequest(order)}
                                                                    disabled={approvingRedoId === order._id}
                                                                    className="rounded-md bg-green-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-green-700 disabled:opacity-50"
                                                                >
                                                                    {approvingRedoId === order._id ? 'Đang duyệt...' : 'Duyệt làm lại'}
                                                                </button>
                                                            )}
                                                            {order.redoRequestStatus === 'pending' && canRequestRedo && (
                                                                <span className="px-2 py-1.5 text-xs text-orange-700">Đang chờ duyệt</span>
                                                            )}
                                                            {order.redoRequestStatus === 'approved' && canRequestRedo && (
                                                                <span className="px-2 py-1.5 text-xs text-green-700">Đã duyệt làm lại</span>
                                                            )}
                                                            {order.refundRequestStatus === 'pending' && canApproveRefund && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => void approveRefundRequest(order)}
                                                                    disabled={approvingRefundId === order._id}
                                                                    className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-red-700 disabled:opacity-50"
                                                                >
                                                                    {approvingRefundId === order._id ? 'Đang duyệt...' : 'Duyệt hoàn tiền'}
                                                                </button>
                                                            )}
                                                            {order.refundRequestStatus === 'pending' && canRequestRefund && (
                                                                <span className="px-2 py-1.5 text-xs text-orange-700">Chờ duyệt hoàn tiền</span>
                                                            )}
                                                            {order.refundRequestStatus === 'processing' && (
                                                                <span className="px-2 py-1.5 text-xs text-blue-700">Đang hoàn tiền...</span>
                                                            )}
                                                            {order.refundRequestStatus === 'approved' && canRequestRefund && (
                                                                <span className="px-2 py-1.5 text-xs text-green-700">Đã hoàn {formatAmount(order.refundAmount || 0)}</span>
                                                            )}
                                                            {canEditPendingOrders && order.status === OrderStatus.PENDING && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => beginEditOrder(order)}
                                                                    className="rounded-md border border-blue-200 bg-white px-3 py-1.5 text-xs font-semibold text-blue-700 transition hover:bg-blue-50"
                                                                    title="Sửa mã và số lượt chạy"
                                                                >
                                                                    Sửa
                                                                </button>
                                                            )}
                                                            {canPrioritizeOrders && order.status === OrderStatus.PENDING && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => void toggleIntegrationPriority(order)}
                                                                    disabled={updatingPriorityId === order._id}
                                                                    aria-label={order.integrationPriority ? 'Bỏ ưu tiên mã chung sức' : 'Ưu tiên mã chung sức'}
                                                                    title={order.integrationPriority ? 'Bỏ ưu tiên' : 'Ưu tiên mã'}
                                                                    className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition disabled:cursor-wait disabled:opacity-50 ${order.integrationPriority ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100' : 'border-gray-200 bg-white text-gray-600 hover:border-amber-300 hover:text-amber-800'}`}
                                                                >
                                                                    {order.integrationPriority ? 'Đang ưu tiên' : 'Ưu tiên'}
                                                                </button>
                                                            )}
                                                            {canCancelOrders && order.status === OrderStatus.PENDING && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => void cancelOrder(order)}
                                                                    disabled={cancellingOrderId === order._id}
                                                                    aria-label="Hủy mã chung sức"
                                                                    title="Hủy mã chung sức"
                                                                    className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-50 disabled:cursor-wait disabled:opacity-50"
                                                                >
                                                                    {cancellingOrderId === order._id ? 'Đang hủy...' : 'Hủy mã'}
                                                                </button>
                                                            )}
                                                        </div>
                                                        {order.note && (
                                                            <p className="mt-2 ml-auto max-w-full truncate text-xs font-normal text-gray-600" title={order.note}>
                                                                Ghi chú: {order.note}
                                                            </p>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                    <div className="flex shrink-0 items-center justify-between border-t border-gray-100 px-4 py-3 text-sm">
                        <span className="text-gray-500">
                            {totalOrders === 0 ? '0 mã chung sức' : `${(page - 1) * ORDERS_PER_PAGE + 1}–${Math.min(page * ORDERS_PER_PAGE, totalOrders)} / ${totalOrders}`}
                            {isLoadingOrders ? ' · Đang tải...' : ''}
                        </span>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                disabled={page <= 1 || isLoadingOrders}
                                onClick={() => setPage((currentPage) => currentPage - 1)}
                                className="rounded-lg border border-gray-200 px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                Trước
                            </button>
                            <span className="px-2 py-1.5 text-gray-600">Trang {page} / {totalPages}</span>
                            <button
                                type="button"
                                disabled={page >= totalPages || isLoadingOrders}
                                onClick={() => setPage((currentPage) => currentPage + 1)}
                                className="rounded-lg border border-gray-200 px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                Sau
                            </button>
                        </div>
                    </div>
                </section>
            </div>
        </div>
        </>
    );
};

export default OrderManagementPage;